import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Button,
  DataTable,
  Badge,
  Text,
  Checkbox,
  TextField,
  Select,
  EmptyState,
  Spinner,
  Toast,
  Frame,
  LegacyStack as Stack,
  Tabs,
  Banner,
  ProgressBar
} from '@shopify/polaris';
import {
  CheckMinor,
  CancelMinor,
  ClockIcon
} from '@shopify/polaris-icons';
import axios from 'axios';

interface CODSettings {
  id: string;
  enabled: boolean;
  autoConfirmation: boolean;
  confirmationTimeout: number;
  requireAddressVerification: boolean;
  sendReminders: boolean;
  reminderTimingHours: number;
  sendPaymentReminder: boolean;
  paymentReminderTiming: number;
  availableTimeSlots: string[];
  totalCODOrders: number;
  successfulDeliveries: number;
  failedDeliveries: number;
}

interface CODOrder {
  id: string;
  orderId: string;
  codAmount: string;
  currency: string;
  confirmed: boolean;
  addressVerified: boolean;
  deliveryStatus: string;
  deliveryDate?: string;
  deliveryTimeSlot?: string;
  createdAt: string;
  order: {
    orderNumber: string;
    totalAmount: string;
  };
  customer: {
    name: string;
    whatsappPhoneNumber: string;
  };
}

interface CODStatistics {
  totalOrders: number;
  confirmedOrders: number;
  deliveredOrders: number;
  failedOrders: number;
  confirmationRate: number;
  successRate: number;
  failureRate: number;
}

export default function CODPage() {
  const [settings, setSettings] = useState<CODSettings | null>(null);
  const [codOrders, setCodOrders] = useState<CODOrder[]>([]);
  const [statistics, setStatistics] = useState<CODStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  const storeId = localStorage.getItem('storeId') || '';
  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchCODData();
  }, []);

  const fetchCODData = async () => {
    try {
      setLoading(true);
      const [settingsRes, ordersRes, statsRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/statistics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSettings(settingsRes.data.data);
      setCodOrders(ordersRes.data.data);
      setStatistics(statsRes.data.data);
    } catch (error) {
      console.error('Failed to fetch COD data:', error);
      showToast('Failed to load COD data');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field: keyof CODSettings, value: any) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/settings`,
        settings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      showToast('Settings saved successfully');
      fetchCODData();
    } catch (error) {
      console.error('Failed to save settings:', error);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmOrder = async (orderId: string) => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/orders/${orderId}/confirm`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      showToast('Order confirmed successfully');
      fetchCODData();
    } catch (error) {
      console.error('Failed to confirm order:', error);
      showToast('Failed to confirm order');
    }
  };

  const handleSendReminder = async (orderId: string) => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/orders/${orderId}/reminder`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      showToast('Reminder sent successfully');
    } catch (error) {
      console.error('Failed to send reminder:', error);
      showToast('Failed to send reminder');
    }
  };

  const handleMarkDelivered = async (orderId: string) => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/cod/orders/${orderId}/delivered`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      showToast('Order marked as delivered');
      fetchCODData();
    } catch (error) {
      console.error('Failed to mark as delivered:', error);
      showToast('Failed to mark as delivered');
    }
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastActive(true);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, any> = {
      PENDING: { status: 'warning', label: 'Pending' },
      CONFIRMED: { status: 'info', label: 'Confirmed' },
      ADDRESS_VERIFIED: { status: 'info', label: 'Address Verified' },
      SCHEDULED: { status: 'attention', label: 'Scheduled' },
      OUT_FOR_DELIVERY: { status: 'attention', label: 'Out for Delivery' },
      DELIVERED: { status: 'success', label: 'Delivered' },
      FAILED: { status: 'critical', label: 'Failed' },
      CANCELLED: { status: 'critical', label: 'Cancelled' },
      RTO: { status: 'critical', label: 'RTO' }
    };

    const config = statusMap[status] || { status: 'info', label: status };
    return <Badge status={config.status}>{config.label}</Badge>;
  };

  const tabs = [
    {
      id: 'dashboard',
      content: 'Dashboard',
      panelID: 'dashboard-panel'
    },
    {
      id: 'orders',
      content: 'Orders',
      panelID: 'orders-panel'
    },
    {
      id: 'settings',
      content: 'Settings',
      panelID: 'settings-panel'
    }
  ];

  const renderDashboard = () => (
    <Layout>
      <Layout.Section>
        <Card>
          <Card.Section>
            <Stack vertical spacing="loose">
              <Text as="h2" variant="headingMd">
                COD Overview
              </Text>

              {!settings?.enabled && (
                <Banner status="warning" title="COD Workflows Disabled">
                  <p>Enable COD workflows in settings to start managing Cash on Delivery orders.</p>
                </Banner>
              )}

              {statistics && (
                <>
                  <Stack distribution="fillEvenly">
                    <Card>
                      <Card.Section>
                        <Stack vertical spacing="tight">
                          <Text as="p" variant="bodyMd" color="subdued">
                            Total COD Orders
                          </Text>
                          <Text as="h3" variant="headingLg">
                            {statistics.totalOrders}
                          </Text>
                        </Stack>
                      </Card.Section>
                    </Card>

                    <Card>
                      <Card.Section>
                        <Stack vertical spacing="tight">
                          <Text as="p" variant="bodyMd" color="subdued">
                            Confirmed
                          </Text>
                          <Text as="h3" variant="headingLg">
                            {statistics.confirmedOrders}
                          </Text>
                          <Text as="p" variant="bodySm" color="subdued">
                            {statistics.confirmationRate}% confirmation rate
                          </Text>
                        </Stack>
                      </Card.Section>
                    </Card>

                    <Card>
                      <Card.Section>
                        <Stack vertical spacing="tight">
                          <Text as="p" variant="bodyMd" color="subdued">
                            Delivered
                          </Text>
                          <Text as="h3" variant="headingLg">
                            {statistics.deliveredOrders}
                          </Text>
                          <Text as="p" variant="bodySm" color="subdued">
                            {statistics.successRate}% success rate
                          </Text>
                        </Stack>
                      </Card.Section>
                    </Card>

                    <Card>
                      <Card.Section>
                        <Stack vertical spacing="tight">
                          <Text as="p" variant="bodyMd" color="subdued">
                            Failed
                          </Text>
                          <Text as="h3" variant="headingLg">
                            {statistics.failedOrders}
                          </Text>
                          <Text as="p" variant="bodySm" color="subdued">
                            {statistics.failureRate}% failure rate
                          </Text>
                        </Stack>
                      </Card.Section>
                    </Card>
                  </Stack>

                  <Card>
                    <Card.Section>
                      <Stack vertical spacing="tight">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          Success Rate
                        </Text>
                        <ProgressBar progress={statistics.successRate} size="small" />
                      </Stack>
                    </Card.Section>
                  </Card>
                </>
              )}
            </Stack>
          </Card.Section>
        </Card>
      </Layout.Section>

      <Layout.Section>
        <Card title="Recent COD Orders">
          <Card.Section>
            {codOrders.length === 0 ? (
              <EmptyState
                heading="No COD orders yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>COD orders will appear here when customers place orders with Cash on Delivery payment method.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Customer', 'Amount', 'Status', 'Confirmed', 'Actions']}
                rows={codOrders.slice(0, 5).map((order) => [
                  order.order.orderNumber,
                  order.customer.name || order.customer.whatsappPhoneNumber,
                  `${order.currency} ${order.codAmount}`,
                  getStatusBadge(order.deliveryStatus),
                  order.confirmed ? <Badge status="success">Yes</Badge> : <Badge>No</Badge>,
                  <Stack spacing="tight">
                    {!order.confirmed && (
                      <Button size="slim" onClick={() => handleConfirmOrder(order.orderId)}>
                        Confirm
                      </Button>
                    )}
                    <Button size="slim" onClick={() => handleSendReminder(order.orderId)}>
                      Remind
                    </Button>
                  </Stack>
                ])}
              />
            )}
          </Card.Section>
        </Card>
      </Layout.Section>
    </Layout>
  );

  const renderOrders = () => (
    <Layout>
      <Layout.Section>
        <Card>
          <Card.Section>
            {codOrders.length === 0 ? (
              <EmptyState
                heading="No COD orders"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>COD orders will appear here.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={[
                  'Order #',
                  'Customer',
                  'Amount',
                  'Status',
                  'Confirmed',
                  'Address Verified',
                  'Delivery Date',
                  'Actions'
                ]}
                rows={codOrders.map((order) => [
                  order.order.orderNumber,
                  <Stack vertical spacing="extraTight">
                    <Text as="span" variant="bodyMd">
                      {order.customer.name || 'Unknown'}
                    </Text>
                    <Text as="span" variant="bodySm" color="subdued">
                      {order.customer.whatsappPhoneNumber}
                    </Text>
                  </Stack>,
                  `${order.currency} ${order.codAmount}`,
                  getStatusBadge(order.deliveryStatus),
                  order.confirmed ? <Badge status="success">Yes</Badge> : <Badge>No</Badge>,
                  order.addressVerified ? <Badge status="success">Yes</Badge> : <Badge>No</Badge>,
                  order.deliveryDate
                    ? new Date(order.deliveryDate).toLocaleDateString()
                    : '-',
                  <Stack spacing="tight">
                    {!order.confirmed && (
                      <Button size="slim" onClick={() => handleConfirmOrder(order.orderId)}>
                        Confirm
                      </Button>
                    )}
                    {order.confirmed && order.deliveryStatus !== 'DELIVERED' && (
                      <Button size="slim" onClick={() => handleMarkDelivered(order.orderId)}>
                        Delivered
                      </Button>
                    )}
                    <Button size="slim" onClick={() => handleSendReminder(order.orderId)}>
                      Remind
                    </Button>
                  </Stack>
                ])}
              />
            )}
          </Card.Section>
        </Card>
      </Layout.Section>
    </Layout>
  );

  const renderSettings = () => (
    <Layout>
      <Layout.Section>
        <Card>
          <Card.Section>
            <Stack vertical spacing="loose">
              <Text as="h2" variant="headingMd">
                COD Workflow Settings
              </Text>

              <Checkbox
                label="Enable COD Workflows"
                checked={settings?.enabled || false}
                onChange={(value) => handleSettingsChange('enabled', value)}
                helpText="Enable or disable COD order processing"
              />

              {settings?.enabled && (
                <>
                  <Checkbox
                    label="Automatic Confirmation Messages"
                    checked={settings.autoConfirmation}
                    onChange={(value) => handleSettingsChange('autoConfirmation', value)}
                    helpText="Automatically send confirmation messages when COD orders are placed"
                  />

                  <TextField
                    label="Confirmation Timeout (hours)"
                    type="number"
                    value={settings.confirmationTimeout.toString()}
                    onChange={(value) => handleSettingsChange('confirmationTimeout', parseInt(value) || 24)}
                    helpText="Hours to wait for customer confirmation"
                    autoComplete="off"
                  />

                  <Checkbox
                    label="Require Address Verification"
                    checked={settings.requireAddressVerification}
                    onChange={(value) => handleSettingsChange('requireAddressVerification', value)}
                    helpText="Ask customers to verify their delivery address"
                  />

                  <Checkbox
                    label="Send Delivery Reminders"
                    checked={settings.sendReminders}
                    onChange={(value) => handleSettingsChange('sendReminders', value)}
                    helpText="Send reminders before delivery"
                  />

                  {settings.sendReminders && (
                    <TextField
                      label="Reminder Timing (hours before delivery)"
                      type="number"
                      value={settings.reminderTimingHours.toString()}
                      onChange={(value) => handleSettingsChange('reminderTimingHours', parseInt(value) || 2)}
                      autoComplete="off"
                    />
                  )}

                  <Checkbox
                    label="Send Payment Preparation Reminders"
                    checked={settings.sendPaymentReminder}
                    onChange={(value) => handleSettingsChange('sendPaymentReminder', value)}
                    helpText="Remind customers to keep cash ready"
                  />

                  {settings.sendPaymentReminder && (
                    <TextField
                      label="Payment Reminder Timing (hours before delivery)"
                      type="number"
                      value={settings.paymentReminderTiming.toString()}
                      onChange={(value) => handleSettingsChange('paymentReminderTiming', parseInt(value) || 24)}
                      autoComplete="off"
                    />
                  )}

                  <Button primary loading={saving} onClick={handleSaveSettings}>
                    Save Settings
                  </Button>
                </>
              )}
            </Stack>
          </Card.Section>
        </Card>
      </Layout.Section>

      <Layout.Section secondary>
        <Card title="How COD Workflows Work">
          <Card.Section>
            <Stack vertical spacing="loose">
              <Text as="p" variant="bodyMd">
                <strong>1. Order Confirmation</strong><br />
                When a COD order is placed, an automatic confirmation message is sent to the customer.
              </Text>

              <Text as="p" variant="bodyMd">
                <strong>2. Address Verification</strong><br />
                Customer confirms their delivery address to reduce failed deliveries.
              </Text>

              <Text as="p" variant="bodyMd">
                <strong>3. Delivery Scheduling</strong><br />
                Customer can select preferred delivery date and time slot.
              </Text>

              <Text as="p" variant="bodyMd">
                <strong>4. Payment Reminder</strong><br />
                Automated reminder to keep exact cash ready before delivery.
              </Text>

              <Text as="p" variant="bodyMd">
                <strong>5. Delivery Tracking</strong><br />
                Track delivery status and mark as delivered/failed.
              </Text>
            </Stack>
          </Card.Section>
        </Card>
      </Layout.Section>
    </Layout>
  );

  if (loading) {
    return (
      <Page title="COD Management">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Frame>
      <Page title="COD Management" subtitle="Manage Cash on Delivery orders and workflows">
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
          {selectedTab === 0 && renderDashboard()}
          {selectedTab === 1 && renderOrders()}
          {selectedTab === 2 && renderSettings()}
        </Tabs>

        {toastActive && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastActive(false)}
            duration={3000}
          />
        )}
      </Page>
    </Frame>
  );
}
